import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { timeAgo } from '../utils/format';
import Icon from '../components/Icon';
import './Notifications.css';

const TYPE_CONFIG = {
  connection_request: {
    icon: 'userPlus',
    color: 'navy',
    getLink: n => `/connections`,
  },
  connection_accepted: {
    icon: 'userCheck',
    color: 'green',
    getLink: n => `/connections`,
  },
  post_like: {
    icon: 'heart',
    color: 'red',
    getLink: n => `/`,   // feed — just go home for now
  },
  post_comment: {
    icon: 'chat',
    color: 'orange',
    getLink: n => `/`,
  },
};

function NotificationItem({ notification, onRead }) {
  const navigate = useNavigate();
  const config = TYPE_CONFIG[notification.type] || { icon: 'bell', color: 'navy', getLink: () => '/' };

  const handleClick = async () => {
    if (!notification.read) {
      await onRead(notification.id);
    }
    navigate(config.getLink(notification));
  };

  return (
    <div
      className={`notif-item ${!notification.read ? 'notif-unread' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
    >
      <div className={`notif-actor-wrap`}>
        {notification.actor_photo ? (
          <img src={notification.actor_photo} alt={notification.actor_name} className="notif-avatar" />
        ) : (
          <div className="notif-avatar notif-avatar-placeholder">
            {notification.actor_name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?'}
          </div>
        )}
        <div className={`notif-type-badge notif-badge-${config.color}`}><Icon name={config.icon} size={12} strokeWidth={2.2} /></div>
      </div>

      <div className="notif-content">
        <p className="notif-title">{notification.title}</p>
        {notification.body && <p className="notif-body">{notification.body}</p>}
        <p className="notif-time">{timeAgo(notification.created_at)}</p>
      </div>

      {!notification.read && <div className="notif-dot" />}
    </div>
  );
}

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (before = null) => {
    try {
      const data = await api.getNotifications(30, before);
      if (!before) {
        setNotifications(data);
      } else {
        setNotifications(prev => [...prev, ...data]);
      }
      setHasMore(data.length === 30);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleReadAll = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error(err);
    }
  };

  const loadMore = () => {
    const last = notifications[notifications.length - 1];
    if (last) load(last.created_at);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div className="notif-page container">
      <div className="notif-header">
        <div>
          <h1>Notifications</h1>
          <p>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-secondary btn-sm" onClick={handleReadAll}>
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="bell" size={24} /></div>
          <p className="empty-state-title">No notifications yet</p>
          <p className="empty-state-text">
            When someone connects with you, likes your post, or comments, you'll see it here.
          </p>
          <Link to="/search" className="btn btn-primary" style={{ marginTop: 16 }}>
            Find Golfers
          </Link>
        </div>
      ) : (
        <div className="notif-list card">
          {notifications.map((n, i) => (
            <React.Fragment key={n.id}>
              {i > 0 && <div className="notif-divider" />}
              <NotificationItem notification={n} onRead={handleRead} />
            </React.Fragment>
          ))}
          {hasMore && (
            <button className="notif-load-more" onClick={loadMore}>
              Load more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Notifications;
